import React, {useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select, {SelectChangeEvent} from "@mui/material/Select";
import {Box, Button, Divider, useMediaQuery} from "@mui/material";
import {useTranslation} from "react-i18next";

import {
    resetCenter,
    resetSelectedState,
    resetSelectedDistrict,
    resetYear,
    resetZoom,
    setCenter,
    setSelectedCountry,
    setSelectedCountryBelongsToOtherRegion,
    setSelectedDistrict,
    setSelectedState,
    setYear,
    setZoom,
} from "../../../../features/map/mapSlice";
import {fetchPinsDataAsync, resetPins} from "../../../../features/map/pinsSlice";

import {AppDispatch, RootState} from "../../../../store/store";
import {availableYears, defaultYear, isRegion, regionEnv} from "../../../../config";

import theme from "../../../../components/styles/theme/theme";
import {USA_COUNTRY_NAMES} from "../../../../features/countries/types";
import {clearStateMap} from "../../../../features/map/stateMapSlice";
import {clearStateStatistics} from "../../../../features/statistics/stateStatisticsSlice";
import {CENTER_MAP, ZOOM_MAP} from "../Map/types";
import {getDistrictsForState} from "../../../../data/usaDistricts";

export const SelectButtons: React.FC = () => {
    const {t} = useTranslation();
    const isSmScreen = useMediaQuery(theme.breakpoints.down("sm"));

    const dispatch = useDispatch<AppDispatch>();
    const {
        map: {selectedCountry, year, defaultCountry, selectedState, selectedDistrict},
        countriesPerUser: {data: countriesData},
        statistics: {data: statisticsData},
    } = useSelector((state: RootState) => state);

    const usaCountry = countriesData.find(c => c.country_name === USA_COUNTRY_NAMES.USA);
    const usaStates = usaCountry?.states ?? [];

    const hideMap = () => {
        const mapContainer = document.getElementById('map-container');
        if (mapContainer && mapContainer.style) {
            mapContainer.style.visibility = 'hidden';
            setTimeout(() => {
                mapContainer.style.visibility = 'unset';
            }, 400);
        }
    };

    const optionsData = [...countriesData];

    const [isYearSelectVisible, setIsYearSelectVisible] = useState(false);

    const districtsForSelectedState = selectedState
        ? getDistrictsForState(selectedState.state_name)
        : [];

    useEffect(() => {
        availableYears.length > 1
            ? setIsYearSelectVisible(true)
            : setIsYearSelectVisible(false);
    }, []);

    const handleCountryChange = (event: SelectChangeEvent) => {
        const {value} = event.target;
        const selectedCountry = countriesData.find(
            (country) => country.country_name.toUpperCase() === value.toUpperCase()
        );
        hideMap();
        dispatch(setSelectedCountry(selectedCountry));
        dispatch(setSelectedCountryBelongsToOtherRegion(undefined));
        dispatch(resetPins());
        dispatch(resetZoom());
        dispatch(resetCenter());
    };

    const handleStateChange = (event: SelectChangeEvent) => {
        const {value} = event.target;
        dispatch(resetSelectedDistrict());
        if (!value) {
            handleStateReset();
            return;
        }
        const state = usaStates.find(s => s.state_id === value);
        if (state) {
            hideMap();
            dispatch(setSelectedState(state));
            const districts = getDistrictsForState(state.state_name);
            if (districts.length === 1) {
                dispatch(setSelectedDistrict(districts[0].district ?? "at-large"));
            }
        }
    };

    const handleDistrictChange = (event: SelectChangeEvent) => {
        const value = event.target.value;
        dispatch(setSelectedDistrict(value || undefined));
    };

    const handleStateReset = () => {
        dispatch(resetSelectedState());
        dispatch(clearStateMap());
        dispatch(clearStateStatistics());
        dispatch(setCenter(CENTER_MAP));
        dispatch(setZoom(ZOOM_MAP));
    };

    const handleYearChange = (event: SelectChangeEvent) => {
        const {value} = event.target;
        dispatch(setYear(value));
        dispatch(resetPins());
        dispatch(resetZoom());
        dispatch(resetCenter());
        if (isRegion.usa) {
            dispatch(resetSelectedState());
            dispatch(clearStateMap());
            dispatch(clearStateStatistics());
        }
    };

    const handleReset = () => {
        dispatch(setSelectedCountry(defaultCountry));
        dispatch(setSelectedCountryBelongsToOtherRegion(undefined));
        dispatch(resetYear());
        dispatch(resetZoom());
        dispatch(resetCenter());
        dispatch(resetPins());
        dispatch(fetchPinsDataAsync(defaultYear));
        if (isRegion.usa) {
            dispatch(resetSelectedState());
            dispatch(resetSelectedDistrict());
            dispatch(clearStateMap());
            dispatch(clearStateStatistics());
        }
    };

    React.useEffect(() => {
        if (!statisticsData || !selectedCountry) return;
        const countryExistInCurrentYear = !!statisticsData.find(
            (country) => {
                return (country.country_name || '').toUpperCase() === (selectedCountry.country_name || '').toUpperCase()
            }
        );
        if (!countryExistInCurrentYear) {
            hideMap();
            dispatch(setSelectedCountry(defaultCountry));
            dispatch(setSelectedCountryBelongsToOtherRegion(undefined));
        }
    }, [defaultCountry, dispatch, selectedCountry, statisticsData, year]);

    const isDifferentCountry =
        selectedCountry &&
        selectedCountry?.country_name.toUpperCase() !== regionEnv;
    const isYearNotDefault = defaultYear !== year;

    const isResetVisible = isDifferentCountry || isYearNotDefault || !!selectedState;

    const selectSx = {
        m: 1,
        p: 1,
        minWidth: 180,
        "& .MuiInputLabel-root": {
            color: "primary.500",
            top: "8px",
            left: "8px",
        },
        "& .MuiOutlinedInput-root": {
            "& fieldset": {borderColor: "primary.300"},
        },
        ".MuiInputBase-input": {color: "primary.500"},
    };

    return (
        <div className='hidden-print'>
            <Box
                gridColumn="span 12"
                display="flex"
                alignItems="center"
                justifyContent="end"
                flexDirection={isSmScreen ? "column" : "row"}
            >
                {isResetVisible && (
                    <>
                        <FormControl sx={{m: 1, p: 1}} size="small">
                            <Button
                                sx={{
                                    color: "custom.blue",
                                    width: "100px",
                                    lineHeight: "24px",
                                    textTransform: "none",
                                }}
                                onClick={handleReset}
                            >
                                {t("common.reset_view")}
                            </Button>
                        </FormControl>
                        <Divider orientation="vertical" flexItem/>
                    </>
                )}
                {isYearSelectVisible && (
                    <FormControl sx={{...selectSx, minWidth: "100px"}} size="small">
                        <InputLabel id="year-label">{t("common.year_label")}</InputLabel>
                        <Select
                            labelId="year-label"
                            id="year-select"
                            value={year}
                            label="Year"
                            onChange={handleYearChange}
                            sx={{maxHeight: 300}}
                        >
                            {availableYears.map((year) => (
                                <MenuItem key={year} value={year}>{year}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}
                {optionsData.length > 1 && (
                    <FormControl sx={selectSx} size="small">
                        <InputLabel id="country-label">{t("common.country_label")}</InputLabel>
                        <Select
                            labelId="country-label"
                            id="country-select"
                            value={selectedCountry ? selectedCountry.country_name : ""}
                            label="Country"
                            onChange={handleCountryChange}
                            MenuProps={{PaperProps: {sx: {maxHeight: 300}}}}
                        >
                            {optionsData
                                .sort((a, b) => a.country_name.localeCompare(b.country_name))
                                .map((country) => (
                                    <MenuItem key={country.iso3} value={country.country_name}>
                                        {country.country_name}
                                    </MenuItem>
                                ))}
                        </Select>
                    </FormControl>
                )}
                {isRegion.usa && usaStates.length > 0 && (
                    <FormControl sx={selectSx} size="small">
                        <InputLabel id="state-label">State</InputLabel>
                        <Select
                            labelId="state-label"
                            id="state-select"
                            value={selectedState?.state_id ?? ""}
                            label="State"
                            onChange={handleStateChange}
                            MenuProps={{PaperProps: {sx: {maxHeight: 300}}}}
                        >
                            <MenuItem value="">
                                <em>All States</em>
                            </MenuItem>
                            {[...usaStates]
                                .sort((a, b) => a.state_name.localeCompare(b.state_name))
                                .map((state) => (
                                    <MenuItem key={state.state_id} value={state.state_id}>
                                        {state.state_name}
                                    </MenuItem>
                                ))}
                        </Select>
                    </FormControl>
                )}
                {isRegion.usa && selectedState && districtsForSelectedState.length > 0 && (
                    <FormControl sx={selectSx} size="small">
                        <InputLabel id="district-label">District</InputLabel>
                        <Select
                            labelId="district-label"
                            id="district-select"
                            value={selectedDistrict ?? ""}
                            label="District"
                            onChange={handleDistrictChange}
                            MenuProps={{PaperProps: {sx: {maxHeight: 300}}}}
                        >
                            <MenuItem value="">
                                <em>All Districts</em>
                            </MenuItem>
                            {districtsForSelectedState.map((d) => (
                                <MenuItem key={d.label} value={d.district ?? "at-large"}>
                                    {d.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}
            </Box>
        </div>
    );
};
